import type { IHttpError } from "Types";
import { setMessage } from "./message/message.slice";
import type { AxiosError } from "axios";
import { setSessionTokenError } from "../app.slice";
import type { AppError } from "../app.types";
import { sessionTokenFailedExceptionBaseMessage } from ".";

export function mapAxiosErrorToHttpError(error: unknown): IHttpError {
  // Handle your custom AppError first
  if (error instanceof Error) {
    const appError = error as AppError;

    return {
      message: appError.message,
      customMessage: appError.customMessage, // include your field
    };
  }

  // Axios error case
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<unknown>;

    return {
      message: axiosError.message,
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data as IHttpError["data"] | undefined,
    };
  }

  return {
    message: "Unexpected error",
  };
}
export interface MetaData {
  translation: object;
}

export const getErrorMessageOfType = (APIErrorType?: string) => {
  // modify this function to return user friendly message based on error type
  return APIErrorType || "default";
};

export const catchError = async (params: {
  e: unknown;
  source?: string | null; // <file name> - <function name>
  logError?: boolean;
}) => {
  const { e, source = null, logError = true } = params;
  console.error(
    "********** Uncaught Exception",
    source ? `in ${source}` : "",
    e,
  );
  if (logError) {
    // log error as needed and remove remove mock Promise
    await Promise.resolve(true);
  }
};

export const handleAPIError = <T>(options: {
  e: unknown;
  dispatch: (param: unknown) => unknown;
  rejectWithValue: (value: IHttpError) => T;
  metaData?: MetaData;
  source?: string;
  failSilent?: boolean;
}) => {
  const { e, dispatch, rejectWithValue, source, failSilent } = options;

  const isAborted =
    (e as IHttpError)?.aborted === true ||
    (e as { name?: string })?.name === "AbortError" ||
    (e as IHttpError)?.message === "canceled";

  // Abort → reject with aborted flag, no UI error
  if (isAborted) {
    return rejectWithValue({ aborted: true });
  }

  // Log only applicable errors
  catchError({ e, source });

  const error = e as {
    message?: string;
    customMessage?: string;
    response?: {
      config?: { responseType?: string };
      data?: {
        type?: string;
        title?: string;
        invalidParams?: { name: string; path: string; reason: string }[];
      };
    };
  };

  let errorType = error.response?.data?.type;

  // handle arraybuffer response
  if (error.response?.config?.responseType === "arraybuffer") {
    try {
      const parsed = JSON.parse(
        new TextDecoder().decode(error.response.data as BufferSource),
      );
      errorType = parsed?.type;
    } catch {
      // ignore parse issues
    }
  }

  // dispatch session token error for handling navigation on UI based on error type
  if (
    (error as AppError)?.customMessage?.includes(
      sessionTokenFailedExceptionBaseMessage,
    )
  ) {
    dispatch(setSessionTokenError(error));
  }

  const defaultMessage =
    "Something went wrong while fetching the data. Please try again.";

  // NoSonar
  // Error details can be added as needed
  // const MessageFromError = error?.customMessage || error?.message;
  // const message = errorType
  //   ? getErrorMessageOfType(errorType)
  //   : MessageFromError || defaultMessage;

  dispatch(
    setMessage({
      message: defaultMessage,
      type: "critical",
      errorType,
      failSilent,
    }),
  );

  return rejectWithValue(mapAxiosErrorToHttpError(e));
};
